/**
 * Iter 126 — Devise d'AFFICHAGE analytique du dashboard.
 *
 * Source de vérité financière : le COMPTE (backend, users.region.currency) —
 * jamais l'IP, le navigateur ni localStorage. La préférence
 * `dashboard_display_currency` (source | CHF | EUR) est persistée côté
 * utilisateur via /api/region/display-currency ; localStorage n'est qu'un
 * miroir pour un rendu immédiat. Le taux BCE (rate + date) vient du backend
 * et est le même que celui utilisé pour les chaînes déjà formatées.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLanguage } from './LanguageContext';
import {
  cacheDisplayPreference, formatCurrency, getCachedDisplayPreference,
  normalizeDisplayChoice, setDisplaySnapshot,
} from '../utils/currency';

const API = process.env.REACT_APP_BACKEND_URL;
export const DisplayCurrencyContext = createContext(null);

const EMPTY = {
  ready: false, sourceCurrency: null, sourceCountry: null, preference: 'source',
  displayCurrency: null, convertible: false, fx: null, currencyContext: null, options: ['source', 'CHF', 'EUR'],
};

function snapshotFrom(state, locale) {
  const cc = state.currencyContext || {};
  return {
    ready: state.ready,
    sourceCurrency: state.sourceCurrency,
    sourceCountry: state.sourceCountry,
    requested: state.preference,
    displayCurrency: state.displayCurrency,
    fx_rate: cc.converting ? cc.fx_rate : null,
    fx_as_of: cc.converting ? cc.fx_as_of : null,
    fx_source: cc.converting ? cc.fx_source : null,
    locale,
  };
}

function fromPayload(d) {
  return {
    ready: true,
    sourceCurrency: (d.source_currency || 'CHF').toUpperCase(),
    sourceCountry: d.source_country || null,
    preference: normalizeDisplayChoice(d.preference) || 'source',
    displayCurrency: (d.display_currency || d.source_currency || 'CHF').toUpperCase(),
    convertible: !!d.convertible,
    fx: d.fx || null,
    currencyContext: d.currency_context || null,
    options: d.options || EMPTY.options,
  };
}

export const DisplayCurrencyProvider = ({ children }) => {
  const { currentLanguage } = useLanguage();
  const [state, setState] = useState(() => ({ ...EMPTY, preference: getCachedDisplayPreference() || 'source' }));
  const [saving, setSaving] = useState(false);
  const loadedFor = useRef(null);

  const apply = useCallback((payload) => {
    const next = fromPayload(payload);
    setState(next);
    cacheDisplayPreference(next.preference);
    setDisplaySnapshot(snapshotFrom(next, currentLanguage));
  }, [currentLanguage]);

  const load = useCallback(async () => {
    const token = window.localStorage.getItem('token');
    if (!token) { loadedFor.current = null; return; }
    try {
      const res = await fetch(`${API}/api/region/display-currency`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`display-currency ${res.status}`);
      loadedFor.current = token;
      apply(await res.json());
    } catch {
      // jamais bloquant : sans contexte de compte, aucune conversion (devise source)
      loadedFor.current = token;
      setState((s) => ({ ...s, ready: true, displayCurrency: null, currencyContext: null }));
      setDisplaySnapshot({ ready: true, displayCurrency: null, fx_rate: null, fx_as_of: null, fx_source: null });
    }
  }, [apply]);

  const ensureLoaded = useCallback(() => {
    const token = window.localStorage.getItem('token');
    if (token && loadedFor.current !== token) load();
  }, [load]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setDisplaySnapshot({ locale: currentLanguage }); }, [currentLanguage]);

  const setPreference = useCallback(async (choice) => {
    const c = normalizeDisplayChoice(choice);
    const token = window.localStorage.getItem('token');
    if (!c || !token) return false;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/region/display-currency`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_currency: c }),
      });
      if (!res.ok) return false;
      apply(await res.json());
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  }, [apply]);

  // Le payload d'un endpoint du périmètre porte le snapshot FX réellement utilisé
  // côté serveur : on l'adopte pour formater les nombres avec le MÊME taux.
  const adoptContext = useCallback((cc) => {
    if (!cc || typeof cc !== 'object') return;
    setState((s) => {
      if (!s.ready) return s;
      const same = s.currencyContext
        && s.currencyContext.fx_rate === cc.fx_rate
        && s.currencyContext.display_currency === cc.display_currency
        && s.currencyContext.source_currency === cc.source_currency;
      if (same) return s;
      const next = {
        ...s,
        sourceCurrency: (cc.source_currency || s.sourceCurrency || 'CHF').toUpperCase(),
        displayCurrency: (cc.display_currency || s.displayCurrency).toUpperCase(),
        currencyContext: cc,
      };
      setDisplaySnapshot(snapshotFrom(next, currentLanguage));
      return next;
    });
  }, [currentLanguage]);

  const value = useMemo(() => {
    const snap = snapshotFrom(state, currentLanguage);
    return {
      ...state,
      saving,
      locale: currentLanguage,
      converting: !!(state.currencyContext && state.currencyContext.converting),
      fxRate: state.currencyContext?.converting ? state.currencyContext.fx_rate : null,
      fxAsOf: state.currencyContext?.converting ? state.currencyContext.fx_as_of : null,
      fxStale: !!state.currencyContext?.fx_stale,
      fmt: (amount, sourceCurrency) => formatCurrency(
        amount, sourceCurrency || snap.sourceCurrency || 'CHF', snap.displayCurrency,
        { source_currency: snap.sourceCurrency, display_currency: snap.displayCurrency, fx_rate: snap.fx_rate, locale: snap.locale, sourceCountry: snap.sourceCountry },
      ),
      setPreference,
      ensureLoaded,
      refresh: load,
      adoptContext,
    };
  }, [state, saving, currentLanguage, setPreference, ensureLoaded, load, adoptContext]);

  return <DisplayCurrencyContext.Provider value={value}>{children}</DisplayCurrencyContext.Provider>;
};

export const useDisplayCurrency = () => {
  const ctx = useContext(DisplayCurrencyContext);
  if (ctx) return ctx;
  return {
    ...EMPTY, saving: false, locale: 'fr', converting: false, fxRate: null, fxAsOf: null, fxStale: false,
    fmt: (amount, sourceCurrency) => formatCurrency(amount, sourceCurrency || 'CHF'),
    setPreference: async () => false, ensureLoaded: () => {}, refresh: async () => {}, adoptContext: () => {},
  };
};
